import { FC, MouseEvent } from "react";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { RemoveButton } from "./ThumbnailsGenerator";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import {
  Checkbox,
  CheckboxLabel,
  CheckboxLabelHolder,
} from "./ThumbnailsCheckbox";
import Text from "@/components/Text";
import { MediaResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 17px;
`;

const Card = styled.div<{ $background: string; $isForeground: boolean }>`
  border-radius: 4px 4px 5px 5px;
  height: 150px;
  width: 100%;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${({ theme }) => theme.colors.white};
  background-image: url(${(props) => props.$background});
  display: flex;
  align-items: flex-end;
  ${({ $isForeground }) =>
    $isForeground &&
    css`
      box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
    `}
`;

const Actions = styled.div<{ $isBackground: boolean }>`
  width: 100%;
  height: 40px;
  border-radius: 0px 0px 4px 4px;
  display: flex;
  align-items: center;
  padding: 0 8px 0 12px;
  justify-content: space-between;
  ${({ $isBackground }) =>
    $isBackground &&
    css`
      background: ${({ theme }) => theme.colors.white};
      box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
    `}
`;

const StyledCheckboxLabelHolder = styled(CheckboxLabelHolder)`
  opacity: 1;
  position: relative;
  left: 0;
  top: 0;
  width: 24px;
`;

const LeftHolder = styled.div`
  display: flex;
  align-items: center;
`;

const StyledText = styled(Text)`
  margin-left: 8px;
`;

const NoItemsCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 0px 0px 4px 4px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoItemsText = styled(Text)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
  }
`;

interface Props {
  images?: MediaResource[];
  type: "background" | "foreground";
  selectedLibraryImages: number[];
  setSelectedLibraryImages: React.Dispatch<React.SetStateAction<number[]>>;
}

const ThumbnailsLibrary: FC<Props> = ({
  images,
  type,
  selectedLibraryImages,
  setSelectedLibraryImages,
}) => {
  const alerts = useAlerts();

  const { teamId } = useParams();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>, id: number) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this ${type}?`
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/teams/${teamId}/media/${id}`
      );

      queryClient.invalidateQueries([`generator_${type}s`, { teamId }], {
        exact: true,
      });

      alerts.success(`${type} was successfully deleted`);
    } catch (err) {
      alerts.error();
    }
  };

  const onChange = (id: number) => {
    setSelectedLibraryImages((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <>
      {!!images?.length ? (
        <GridLayout>
          {images?.map((image, index) => (
            <Card
              key={index}
              $isForeground={type === "foreground"}
              $background={image.versions.original}
            >
              <Actions $isBackground={type === "background"}>
                <LeftHolder>
                  <Checkbox
                    id={`checkbox-${image.id}`}
                    name="thumbnails"
                    type="checkbox"
                    checked={selectedLibraryImages.includes(image.id)}
                    onChange={() => onChange(image.id)}
                  />
                  <StyledCheckboxLabelHolder>
                    <CheckboxLabel htmlFor={`checkbox-${image.id}`} />
                  </StyledCheckboxLabelHolder>
                  <StyledText $type="extraSmall" $color="gray">
                    {selectedLibraryImages.includes(image.id)
                      ? "Active"
                      : "Inactive"}
                  </StyledText>
                </LeftHolder>
                <RemoveButton onClick={(e) => onDelete(e, image.id)}>
                  <TrashIcon />
                </RemoveButton>
              </Actions>
            </Card>
          ))}
        </GridLayout>
      ) : (
        <NoItemsCard>
          <NoItemsText $type="caption" $color="gray">
            <svg
              width="23"
              height="20"
              viewBox="0 0 23 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.5354 1.66666H2.46401C2.01034 1.66666 1.64258 2.03975 1.64258 2.49999V17.5C1.64258 17.9602 2.01034 18.3333 2.46401 18.3333H20.5354C20.9891 18.3333 21.3569 17.9602 21.3569 17.5V2.49999C21.3569 2.03975 20.9891 1.66666 20.5354 1.66666Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1.64258 14.1666L6.81173 8.92258C6.888 8.84519 6.97856 8.78381 7.07822 8.74193C7.17788 8.70005 7.28469 8.6785 7.39256 8.6785C7.50043 8.6785 7.60725 8.70005 7.70691 8.74193C7.80657 8.78381 7.89712 8.84519 7.9734 8.92258L12.5617 13.5774C12.638 13.6548 12.7286 13.7162 12.8282 13.758C12.9279 13.7999 13.0347 13.8215 13.1426 13.8215C13.2504 13.8215 13.3573 13.7999 13.4569 13.758C13.5566 13.7162 13.6471 13.6548 13.7234 13.5774L15.8474 11.4226C15.9237 11.3452 16.0143 11.2838 16.1139 11.2419C16.2136 11.2001 16.3204 11.1785 16.4283 11.1785C16.5361 11.1785 16.643 11.2001 16.7426 11.2419C16.8423 11.2838 16.9328 11.3452 17.0091 11.4226L21.3568 15.8333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.3747 8.33334C15.0552 8.33334 15.6069 7.7737 15.6069 7.08334C15.6069 6.39299 15.0552 5.83334 14.3747 5.83334C13.6942 5.83334 13.1426 6.39299 13.1426 7.08334C13.1426 7.7737 13.6942 8.33334 14.3747 8.33334Z"
                fill="currentColor"
              />
            </svg>
            No items found
          </NoItemsText>
        </NoItemsCard>
      )}
    </>
  );
};

export default ThumbnailsLibrary;
